import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Sailboat, Waves } from "lucide-react";
import PageTransition from "@/components/mobile/PageTransition";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getCloudBySlug } from "@/components/weather/cloudTypesData";
import MissingRoute from "@/lib/MissingRoute";

export default function CloudDetail() {
  const { cloudSlug } = useParams();
  const cloud = getCloudBySlug(cloudSlug);

  if (!cloud) return <MissingRoute />;

  const Icon = cloud.icon;

  return (
    <PageTransition>
      <main className="min-h-screen bg-[radial-gradient(circle_at_12%_0%,rgba(20,184,166,0.20),transparent_28rem),linear-gradient(180deg,#F3FBFA_0%,#F8FCFB_100%)] px-4 py-8 text-slate-950 sm:px-6 md:px-8 lg:py-10">
        <div className="mx-auto w-full max-w-5xl space-y-5">
          <Button asChild variant="outline" className="bg-white/70">
            <Link to="/weather/cloud-types">
              <ArrowLeft className="h-4 w-4" /> All cloud types
            </Link>
          </Button>

          <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 p-5 text-white shadow-2xl shadow-ocean/20 sm:p-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] !text-cyan-200">Cloud Name</p>
                <h1 className="mt-2 text-4xl font-black tracking-tight !text-white sm:text-5xl">{cloud.name}</h1>
                <p className="mt-3 max-w-3xl text-base leading-7 !text-cyan-50/90">{cloud.short}</p>
              </div>
              <div className="grid h-16 w-16 shrink-0 place-items-center rounded-3xl bg-cyan-300/15 text-cyan-100 ring-1 ring-cyan-200/20">
                <Icon className="h-8 w-8" />
              </div>
            </div>
          </section>

          <div className="grid gap-4 md:grid-cols-3">
            <Card className="rounded-[1.5rem] border-white/60 bg-white/90">
              <CardContent className="p-5">
                <Eye className="h-6 w-6 text-[#247C78]" />
                <h2 className="mt-3 text-lg font-black">What it looks like</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{cloud.traits}</p>
              </CardContent>
            </Card>
            <Card className="rounded-[1.5rem] border-white/60 bg-white/90">
              <CardContent className="p-5">
                <Waves className="h-6 w-6 text-[#247C78]" />
                <h2 className="mt-3 text-lg font-black">Simple meaning</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{cloud.simpleMeaning}</p>
              </CardContent>
            </Card>
            <Card className="rounded-[1.5rem] border-white/60 bg-white/90">
              <CardContent className="p-5">
                <Sailboat className="h-6 w-6 text-[#247C78]" />
                <h2 className="mt-3 text-lg font-black">For boating</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{cloud.marineMeaning}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-[1.5rem] border-white/60 bg-white/90">
            <CardContent className="p-5">
              <h2 className="text-xl font-black">How to recognize {cloud.name}</h2>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {cloud.lookFor.map((item) => (
                  <li key={item} className="rounded-2xl bg-cyan-50 px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-cyan-100">
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </PageTransition>
  );
}